"use client";

import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Switch } from "@/app/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { 
  BarChart3,
  Link,
  Clock,
  Shield,
  Hash,
} from "lucide-react";
import { QRState } from "./types";

interface DynamicQRProps {
  state: QRState;
  updateState: (update: Partial<QRState>) => void;
  onCreateDynamic: () => void;
  isGenerating: boolean;
}

export default function DynamicQR({
  state,
  updateState,
  onCreateDynamic,
  isGenerating,
}: DynamicQRProps) {
  return (
    <Card className="card-enhanced">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Dynamic QR Code
        </CardTitle>
        <CardDescription>
          Create trackable QR codes with analytics
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="flex items-center space-x-2">
          <Switch
            id="showDynamic"
            checked={state.showDynamic}
            onCheckedChange={(checked) => updateState({ showDynamic: checked })}
          />
          <Label htmlFor="showDynamic">Enable Dynamic QR Code</Label>
        </div>

        {state.showDynamic && (
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Settings</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dynamicUrl">Target URL</Label>
                <Input
                  id="dynamicUrl"
                  type="url"
                  placeholder="https://example.com"
                  value={state.dynamicUrl}
                  onChange={(e) => updateState({ dynamicUrl: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dynamicSlug">Custom Slug (Optional)</Label>
                <Input
                  id="dynamicSlug"
                  placeholder="my-custom-slug"
                  value={state.dynamicSlug}
                  onChange={(e) => updateState({ dynamicSlug: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for auto-generated slug
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="advanced" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dynamicExpiresAt">Expiration Date</Label>
                <Input
                  id="dynamicExpiresAt"
                  type="datetime-local"
                  value={state.dynamicExpiresAt}
                  onChange={(e) => updateState({ dynamicExpiresAt: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dynamicMaxScans">Maximum Scans</Label>
                <Input
                  id="dynamicMaxScans"
                  type="number"
                  min="0"
                  placeholder="0 (unlimited)"
                  value={state.dynamicMaxScans || ''}
                  onChange={(e) => updateState({ dynamicMaxScans: parseInt(e.target.value) || 0 })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dynamicPassword">Password Protection</Label>
                <Input
                  id="dynamicPassword"
                  type="password"
                  placeholder="Optional password"
                  value={state.dynamicPassword}
                  onChange={(e) => updateState({ dynamicPassword: e.target.value })}
                />
              </div>
            </TabsContent>
          </Tabs>
        )}

        {state.showDynamic && (
          <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Dynamic QR Benefits
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <BarChart3 className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium">Analytics</div>
                  <div className="text-muted-foreground">Track scans, locations, and devices</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Link className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium">URL Management</div>
                  <div className="text-muted-foreground">Change destination without reprinting</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium">Expiration</div>
                  <div className="text-muted-foreground">Set automatic expiration dates</div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Shield className="h-4 w-4 text-blue-600 mt-0.5" />
                <div>
                  <div className="font-medium">Security</div>
                  <div className="text-muted-foreground">Password protection and scan limits</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {state.showDynamic && (
          <Button
            onClick={onCreateDynamic}
            disabled={isGenerating}
            className="w-full btn-enhanced hover-lift"
          >
            {isGenerating ? 'Creating Dynamic QR...' : 'Create Dynamic QR Code'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
